import { useHubble } from '@seek/hubble';
import { getJobDetailsMetadata } from '@seek/sol-js';
import { useCallback, useEffect, useMemo, useState } from 'react';

import type { JobDetails } from 'src/graphql/graphql';

interface Options {
  job: JobDetails['job'];
  searchRequestToken?: string;
  isSalaryMatch: boolean;
  isAddSalaryPrefNudgeShown: boolean;
}

type UseApplyTracking = (options: Options) => [string, () => void];

export const useApplyTracking: UseApplyTracking = ({
  job,
  searchRequestToken,
  isSalaryMatch,
  isAddSalaryPrefNudgeShown,
}) => {
  const { id: jobId, isLinkOut } = job;
  const hubble = useHubble();

  const getTrackingInfo = useCallback<() => [string, any]>(() => {
    const queryString = searchRequestToken ? `?sol=${searchRequestToken}` : '';
    const urlString = `/job/${jobId}/apply${queryString}`;
    const solMetaData =
      typeof window !== 'undefined'
        ? getJobDetailsMetadata({
            jobId,
            token: searchRequestToken,
          })
        : {};

    return [urlString, solMetaData];
  }, [searchRequestToken, jobId]);

  const [initialUrlStr, solMeta] = useMemo(getTrackingInfo, [getTrackingInfo]);

  const [urlStr, setUrlStr] = useState(initialUrlStr);
  useEffect(() => {
    const [url] = getTrackingInfo();
    setUrlStr(url);
  }, [getTrackingInfo, setUrlStr]);

  const onApplyClick = useCallback(() => {
    hubble.trackEvent('job_apply_pressed', {
      jobId,
      linkout: isLinkOut,
      thirdPartyLink: isLinkOut,
      solMetadata: solMeta,
      isAddSalaryPrefNudgeShown,
      isSalaryMatch,
    });
  }, [
    solMeta,
    hubble,
    jobId,
    isLinkOut,
    isAddSalaryPrefNudgeShown,
    isSalaryMatch,
  ]);

  return [urlStr, onApplyClick];
};
